import { Component, ElementRef, inject, Input, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { filter, map, mergeMap, take, tap } from 'rxjs';
import { VipLevel, vipLevelAssetsMapping } from '../../../shared/models/assets.model';
import { AuthService, AuthState } from '../../../shared/services';
import { ApiService } from '../../../shared/stores/api';
import { SettingsService } from '../../../shared/stores/settings/settings.service';
import { listenForLargeScreen } from '../../../shared/utils';
import { getNavLinksForUserType } from './navigation-items';

import { Models } from '$models';
import { AsyncPipe, NgStyle } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { DesktopBuySellListComponent } from './desktop-buy-sell-list/desktop-buy-sell-list.component';

@UntilDestroy()
@Component({
  selector: 'app-nav',
  styleUrls: ['./nav.component.scss'],
  templateUrl: './nav.component.html',
  encapsulation: ViewEncapsulation.None,

  imports: [FontAwesomeModule, DesktopBuySellListComponent, RouterLink, TooltipModule, ButtonModule, NgStyle, PopoverModule, SharedModule, AsyncPipe],
})
export class NavComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly api = inject(ApiService);
  private readonly renderer = inject(Renderer2);
  @Input() isHomePage: boolean | null = false;
  @ViewChild(Popover) op!: Popover;
  @ViewChild('profile', { static: false }) profile!: ElementRef;
  /** Turn the username into title case */
  public readonly userName$ = this.auth.userName$;
  public readonly user$ = this.settingsService.user$;
  public readonly loggedIn$ = this.auth.isLoggedIn$;
  public readonly userTypes = Models.UserType;

  public filterVisible = false;
  public readonly userType$ = this.auth.userType$;
  public readonly buySellDropdownItems$ = this.auth.userType$.pipe(map(userType => getNavLinksForUserType(userType)));
  public readonly searchRouteSlug = Models.SearchRouteSlug;

  public isLarge = false;
  public readonly largeScreenObserver$ = listenForLargeScreen().pipe(tap(isLarge => (this.isLarge = isLarge)));

  public readonly vipLevelAssetData = this.auth.isLoggedIn$.pipe(
    take(1),
    filter(x => !!x),
    mergeMap(() =>
      this.api.viplevels.select$.pipe(
        filter(state => !!state),
        map(state => state!.VIPLEVEL),
        map((vipLevel: VipLevel) => {
          if (vipLevel === VipLevel.NOLEVEL) {
            return null;
          }
          return vipLevelAssetsMapping[vipLevel];
        }),
      ),
    ),
  );

  logOut() {
    this.auth.logOut(AuthState.loggedOut, { triggeredByUser: true });
  }

  login() {
    const returnUrl = this.router.url !== '/' && this.router.url !== '/login' ? this.router.url.split('?')[0] : null;
    this.router.navigate(['/login'], { queryParams: { currentUrl: returnUrl } });
  }

  navigateToWatchList() {
    this.auth.isLoggedIn$.pipe(take(1)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.router.navigate(['/saved-properties']);
      } else {
        this.router.navigate(['/login'], { queryParams: { currentUrl: '/saved-properties', target: 'watchlist' } });
      }
    });
  }

  navigateToAlertsNotifications() {
    this.auth.isLoggedIn$.pipe(take(1)).subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.router.navigate(['/my-profile/alerts-notifications']);
      } else {
        this.router.navigate(['/login'], { queryParams: { currentUrl: '/my-profile/alerts-notifications', target: 'alerts-notifications' } });
      }
    });
  }

  navigateToSearchHouses() {
    this.router.navigate(['/' + this.searchRouteSlug]);
  }

  showProfileSettings(event: any) {
    if (this.isLarge) {
      this.op.toggle(event);
    } else {
      this.router.navigate(['/mobile/user-profile']);
    }
  }
}
